<script setup>
// NOTICE: KAPTION-INTEGRATION — Reusable metric stat card for campaign reports
defineProps({
  label: { type: String, required: true },
  value: { type: Number, default: 0 },
  percentage: { type: Number, default: null },
  variant: {
    type: String,
    default: 'slate',
    validator: v => ['slate', 'teal', 'green', 'ruby'].includes(v),
  },
  icon: { type: String, default: '' },
  // NOTICE: KAPTION-INTEGRATION — optional hover tooltip rows (per-link click
  // breakdown): [{ title, destination, count, dim }]
  tooltipRows: { type: Array, default: null },
  tooltipTitle: { type: String, default: '' },
});

const VARIANT_CLASSES = {
  slate: {
    bar: 'bg-n-slate-9',
    icon: 'text-n-slate-9',
    text: 'text-n-slate-11',
  },
  teal: {
    bar: 'bg-n-teal-9',
    icon: 'text-n-teal-9',
    text: 'text-n-teal-11',
  },
  green: {
    bar: 'bg-n-green-9',
    icon: 'text-n-green-9',
    text: 'text-n-green-11',
  },
  ruby: {
    bar: 'bg-n-ruby-9',
    icon: 'text-n-ruby-9',
    text: 'text-n-ruby-11',
  },
};
</script>

<template>
  <div
    class="relative flex flex-col gap-2 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
    :class="tooltipRows && tooltipRows.length ? 'group cursor-help' : ''"
  >
    <div class="flex items-center gap-2">
      <span
        v-if="icon"
        class="w-4 h-4"
        :class="[icon, VARIANT_CLASSES[variant].icon]"
      />
      <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">
        {{ label }}
      </span>
      <span
        v-if="tooltipRows && tooltipRows.length"
        class="i-lucide-info w-3.5 h-3.5 ml-auto text-n-slate-9"
      />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION — hover tooltip (clicks-by-link breakdown) -->
    <div
      v-if="tooltipRows && tooltipRows.length"
      class="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-30 hidden group-hover:block min-w-64 rounded-xl border border-n-weak bg-n-solid-1 shadow-xl p-3"
    >
      <div
        v-if="tooltipTitle"
        class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wide mb-2"
      >
        {{ tooltipTitle }}
      </div>
      <div
        v-for="(row, idx) in tooltipRows"
        :key="idx"
        class="flex items-start justify-between gap-4 py-1.5 border-t border-n-weak first:border-t-0"
        :class="row.dim ? 'opacity-60' : ''"
      >
        <div class="min-w-0">
          <div class="text-xs font-semibold text-n-slate-12">
            {{ row.title }}
          </div>
          <div
            v-if="row.destination"
            class="text-[11px] text-n-slate-10 truncate max-w-48"
            :title="row.destination"
          >
            {{ row.destination }}
          </div>
        </div>
        <span class="text-xs font-bold text-n-green-11">{{ row.count }}</span>
      </div>
    </div>
    <span class="text-2xl font-bold text-n-slate-12">
      {{ value.toLocaleString() }}
    </span>
    <div v-if="percentage !== null" class="flex flex-col gap-1">
      <div class="w-full h-1.5 rounded-full bg-n-alpha-black4 overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-500"
          :class="VARIANT_CLASSES[variant].bar"
          :style="{ width: `${Math.min(percentage, 100)}%` }"
        />
      </div>
      <span class="text-xs font-medium" :class="VARIANT_CLASSES[variant].text">
        {{ percentage }}%
      </span>
    </div>
  </div>
</template>
